import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Grid,
  Heading,
  Skeleton,
  Text,
} from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { WebSocketHelper } from 'classes/helpers/web-socket.helper';
import { CommonDialog } from 'components/common/dialogs';
import { CopyPitchesDialog } from 'components/common/dialogs/copy-pitches';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonFormGrid } from 'components/common/form/grid';
import { CommonSelectInput } from 'components/common/form/select';
import { CommonSliderInput } from 'components/common/form/slider';
import { CommonTextInput } from 'components/common/form/text';
import { JsonViewer } from 'components/common/json/json-viewer';
import { CommonVideoPreview } from 'components/common/video/preview';
import { MachineCalibrateButton } from 'components/machine/buttons/calibrate';
import { MachineInspectButton } from 'components/machine/buttons/inspect';
import usePitchListStore from 'components/sections/pitch-list/store/use-pitch-list-store';
import { AimingContext, AimingProvider } from 'contexts/aiming.context';
import { MachineContext, MachineDialogMode } from 'contexts/machine.context';
import { addSeconds, isPast, lightFormat } from 'date-fns';
import { ResetPlateMode } from 'enums/machine.enums';
import { t } from 'i18next';
import { IBaseDialog } from 'interfaces/i-dialogs';
import { MachineHelper } from 'lib_ts/classes/machine.helper';
import { VISIBLE_STRIKEZONE } from 'lib_ts/classes/plate.helper';
import { StaticVideoType, WsMsgType } from 'lib_ts/enums/machine-msg.enum';
import { MS_LIMITS } from 'lib_ts/enums/machine.enums';
import { BuildPriority, DEFAULT_DELIVERY } from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import {
  DEFAULT_MACHINE_STATE,
  IMachineState,
} from 'lib_ts/interfaces/i-machine-state';
import { IFireMsg } from 'lib_ts/interfaces/machine-msg/i-fire';
import { IMachineStateMsg } from 'lib_ts/interfaces/machine-msg/i-machine-state';
import { SpecialMsPosition } from 'lib_ts/interfaces/machine-msg/i-special-mstarget';
import { IPitch } from 'lib_ts/interfaces/pitches';
import { clone } from 'lodash';
import { FC, useContext, useEffect, useState } from 'react';
import { StateTransformService } from 'services/state-transform.service';
import { WebSocketService } from 'services/web-socket.service';
import useMachineStatusStore from 'stores/use-machine-status-store';
import { useVideosStore } from 'stores/use-videos-store';
import { v4 } from 'uuid';

const COMPONENT_NAME = 'MSDebugDialog';

/** how old mscurrent needs to be before updating with newer mscurrent */
const UPDATE_MSCURRENT_SEC = 2;

// user may input "-" or "." while typing a negative or decimal number, so allow these to be strings during input but convert to numbers before sending to machine
type IMachineStateInput = Omit<IMachineStateMsg, 'qw' | 'qx' | 'qy' | 'qz'> & {
  qw: number | string;
  qx: number | string;
  qy: number | string;
  qz: number | string;
};

interface IBaseProps extends IBaseDialog {
  onClose: () => void;
}

const getDefaultMS = (py: number): IMachineStateMsg => {
  const output: IMachineStateMsg = {
    ...DEFAULT_MACHINE_STATE,
    hash: null,
    force: null,
    jitter: null,
    pitch_info: null,
    video_uuid: StaticVideoType.training_short,
    training: false,
    w1: 2100,
    w2: 1800,
    w3: 1800,
    a1: 0,
    a2: 0,
    a3: 0,
    tilt: 2.5,
    yaw: 0,
    px: 0,
    py: py,
    pz: 4.5,
  };

  return output;
};

const CARD_SIZE = '3';
const SUBHEADER_SIZE = '2';

const DECIMALS = {
  ALPHAS: 1,
  POSITION: 1,
  TILT: 1,
  WHEELS: 0,
  YAW: 1,
};

const STEPS = {
  ALPHAS: Math.pow(10, -DECIMALS.ALPHAS),
  POSITION: Math.pow(10, -DECIMALS.POSITION),
  TILT: Math.pow(10, -DECIMALS.TILT),
  WHEELS: Math.pow(10, -DECIMALS.WHEELS),
  YAW: Math.pow(10, -DECIMALS.YAW),
};

// ensure seam inputs are properly typed before sending to machine
const getMsTarget = (ms: IMachineStateInput): IMachineStateMsg => {
  const coerceNum = (v: string | number): number => Number(v) || 0;

  const output = clone(ms);

  output.qw = coerceNum(ms.qw);
  output.qx = coerceNum(ms.qx);
  output.qy = coerceNum(ms.qy);
  output.qz = coerceNum(ms.qz);

  return output as IMachineStateMsg;
};

export const MSDebugDialogHoC = (props: IBaseProps) => {
  return (
    <AimingProvider identifier="MsDebugAimingCx" training={false}>
      <MSDebugDialog {...props} />
    </AimingProvider>
  );
};

const MSDebugDialog: FC<IBaseProps> = (props) => {
  const calibrated = useMachineStatusStore((s) => s.calibrated);

  const {
    activeModel,
    machine,
    loading: loadingMachine,
    closeDialog,
    getDefaultPitch,
    sendRawTarget,
    specialMsTarget,
  } = useContext(MachineContext);

  const { setAndSendForBP } = useContext(AimingContext);

  const [msCurrent, setMsCurrent] = useState<Partial<IMachineStateMsg>>();
  const [msCurrentDate, setMsCurrentDate] = useState<Date>();

  const [msTarget, setMsTarget] = useState<IMachineStateInput>(
    getDefaultMS(machine.releaseFt)
  );

  const updateMsTarget = async (value: Partial<IMachineStateInput>) => {
    /** reset video if illegal for given px value */
    if (value.px !== undefined) {
      const { options: videoOptions } = useVideosStore
        .getState()
        .getVideoOptions({
          releasePx: value.px,
        });

      const video = videoOptions.find((v) => v.value === msTarget.video_uuid);

      if (!video) {
        value.video_uuid = '';
      }
    }

    const tempPitch = getDefaultPitch(
      activeModel?.supports_spins ? BuildPriority.Spins : BuildPriority.Breaks
    );

    tempPitch._id = `temp-${v4()}`;

    // set both because who cares for a debug msg
    tempPitch.stretchVideoID = msTarget.video_uuid;
    tempPitch.windupVideoID = msTarget.video_uuid;

    await setAndSendForBP({
      pitch: tempPitch,
      resetPlate: ResetPlateMode.Skip,
      sendConfig: undefined,
    });

    setMsTarget((prev) => ({ ...prev, ...value }));
  };

  const [dialogSave, setDialogSave] = useState<{
    key: number;
    pitch: Partial<IPitch>;
  }>();

  // MARK: monitor mscurrent
  useEffect(() => {
    const callback = (event: CustomEvent) => {
      const data: IMachineStateMsg = event.detail;
      if (
        !msCurrentDate ||
        isPast(addSeconds(msCurrentDate, UPDATE_MSCURRENT_SEC))
      ) {
        setMsCurrent(data);
        setMsCurrentDate(new Date());
      }
    };

    WebSocketHelper.on(WsMsgType.M2U_MsCurrent, callback);

    return () => {
      WebSocketHelper.remove(WsMsgType.M2U_MsCurrent, callback);
    };
  }, [msCurrentDate]);

  const [delivery, setDelivery] = useState(DEFAULT_DELIVERY);

  // MARK: component
  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <CommonDialog
        identifier={props.identifier}
        width={RADIX.DIALOG.WIDTH.XL}
        title={
          <Flex gap={RADIX.FLEX.GAP.SM}>
            <Heading size={RADIX.HEADING.SIZE.MD}>Machine Debugger</Heading>
            <Badge color={RADIX.COLOR.WARNING}>{machine.machineID}</Badge>
          </Flex>
        }
        loading={loadingMachine}
        content={
          <Grid columns="2" gap={RADIX.FLEX.GAP.FORM}>
            <Box gridColumn="span 2">
              <Text size={RADIX.TEXT.SIZE.XS}>
                Last Updated:{' '}
                {msCurrentDate
                  ? lightFormat(msCurrentDate, 'h:mm:ss a')
                  : '???'}
              </Text>
            </Box>

            <Card size={CARD_SIZE}>
              <Flex direction="column" gap={RADIX.FLEX.GAP.FORM}>
                <Heading size={SUBHEADER_SIZE}>Wheels</Heading>

                <CommonSliderInput
                  id="w1"
                  value={msTarget.w1}
                  min={0} // *special request from Aaron
                  max={MS_LIMITS.WHEEL_SPEED.MAX}
                  step={STEPS.WHEELS}
                  onChange={(v) => updateMsTarget({ w1: v })}
                  hint_md={`Current: ${
                    msCurrent?.w1?.toFixed(DECIMALS.WHEELS) ?? '???'
                  }`}
                  stepperLabel="w1"
                  stepperDecimals={DECIMALS.WHEELS}
                  showStepper
                />
                <CommonSliderInput
                  id="w2"
                  value={msTarget.w2}
                  min={0} // *special request from Aaron
                  max={MS_LIMITS.WHEEL_SPEED.MAX}
                  step={STEPS.WHEELS}
                  onChange={(v) => updateMsTarget({ w2: v })}
                  hint_md={`Current: ${
                    msCurrent?.w2?.toFixed(DECIMALS.WHEELS) ?? '???'
                  }`}
                  stepperLabel="w2"
                  stepperDecimals={DECIMALS.WHEELS}
                  showStepper
                />
                <CommonSliderInput
                  id="w3"
                  value={msTarget.w3}
                  min={0} // *special request from Aaron
                  max={MS_LIMITS.WHEEL_SPEED.MAX}
                  step={STEPS.WHEELS}
                  onChange={(v) => updateMsTarget({ w3: v })}
                  hint_md={`Current: ${
                    msCurrent?.w3?.toFixed(DECIMALS.WHEELS) ?? '???'
                  }`}
                  stepperLabel="w3"
                  stepperDecimals={DECIMALS.WHEELS}
                  showStepper
                />
              </Flex>
            </Card>

            <Card size={CARD_SIZE}>
              <Flex direction="column" gap={RADIX.FLEX.GAP.FORM}>
                <Heading size={SUBHEADER_SIZE}>Alphas</Heading>

                <CommonSliderInput
                  id="a1"
                  value={msTarget.a1}
                  min={MS_LIMITS.ALPHAS.MIN}
                  max={MS_LIMITS.ALPHAS.MAX}
                  step={STEPS.ALPHAS}
                  onChange={(v) => updateMsTarget({ a1: v })}
                  hint_md={`Current: ${
                    msCurrent?.a1?.toFixed(DECIMALS.ALPHAS) ?? '???'
                  }`}
                  stepperLabel="a1"
                  stepperDecimals={DECIMALS.ALPHAS}
                  showStepper
                />
                <CommonSliderInput
                  id="a2"
                  value={msTarget.a2}
                  min={MS_LIMITS.ALPHAS.MIN}
                  max={MS_LIMITS.ALPHAS.MAX}
                  step={STEPS.ALPHAS}
                  onChange={(v) => updateMsTarget({ a2: v })}
                  hint_md={`Current: ${
                    msCurrent?.a2?.toFixed(DECIMALS.ALPHAS) ?? '???'
                  }`}
                  stepperLabel="a2"
                  stepperDecimals={DECIMALS.ALPHAS}
                  showStepper
                />
                <CommonSliderInput
                  id="a3"
                  value={msTarget.a3}
                  min={MS_LIMITS.ALPHAS.MIN}
                  max={MS_LIMITS.ALPHAS.MAX}
                  step={STEPS.ALPHAS}
                  onChange={(v) => updateMsTarget({ a3: v })}
                  hint_md={`Current: ${
                    msCurrent?.a3?.toFixed(DECIMALS.ALPHAS) ?? '???'
                  }`}
                  stepperLabel="a3"
                  stepperDecimals={DECIMALS.ALPHAS}
                  showStepper
                />
              </Flex>
            </Card>

            <Card size={CARD_SIZE}>
              <Flex direction="column" gap={RADIX.FLEX.GAP.FORM}>
                <Heading size={SUBHEADER_SIZE}>Tilt & Yaw</Heading>

                <CommonSliderInput
                  id="tilt"
                  value={msTarget.tilt}
                  min={MS_LIMITS.TILT.MIN}
                  max={MS_LIMITS.TILT.MAX}
                  step={STEPS.TILT}
                  onChange={(v) => updateMsTarget({ tilt: v })}
                  hint_md={`Current: ${
                    msCurrent?.tilt?.toFixed(DECIMALS.TILT) ?? '???'
                  }`}
                  stepperLabel="tilt"
                  stepperDecimals={DECIMALS.TILT}
                  showStepper
                />

                <CommonSliderInput
                  id="yaw"
                  value={msTarget.yaw}
                  min={MS_LIMITS.YAW.MIN}
                  max={MS_LIMITS.YAW.MAX}
                  step={STEPS.YAW}
                  onChange={(v) => updateMsTarget({ yaw: v })}
                  hint_md={`Current: ${
                    msCurrent?.yaw?.toFixed(DECIMALS.YAW) ?? '???'
                  }`}
                  stepperLabel="yaw"
                  stepperDecimals={DECIMALS.YAW}
                  showStepper
                />
              </Flex>
            </Card>

            <Card size={CARD_SIZE}>
              <Flex direction="column" gap={RADIX.FLEX.GAP.FORM}>
                <Heading size={SUBHEADER_SIZE}>Position</Heading>

                <CommonSliderInput
                  id="px"
                  value={msTarget.px}
                  min={MS_LIMITS.POSITION.X.MIN}
                  max={MS_LIMITS.POSITION.X.MAX}
                  step={STEPS.POSITION}
                  onChange={(v) => updateMsTarget({ px: v })}
                  hint_md={`Current: ${
                    msCurrent?.px?.toFixed(DECIMALS.POSITION) ?? '???'
                  }`}
                  stepperLabel="px"
                  stepperDecimals={DECIMALS.POSITION}
                  showStepper
                />
                <CommonSliderInput
                  id="py"
                  value={msTarget.py}
                  min={0}
                  max={80}
                  step={STEPS.POSITION}
                  onChange={(v) => updateMsTarget({ py: v })}
                  hint_md={`Current: ${
                    msCurrent?.py?.toFixed(DECIMALS.POSITION) ?? '???'
                  }`}
                  stepperLabel="py"
                  stepperDecimals={DECIMALS.POSITION}
                  showStepper
                  disabled
                />
                <CommonSliderInput
                  id="pz"
                  value={msTarget.pz}
                  min={4} // *special request from Aaron
                  max={MS_LIMITS.POSITION.Z.MAX}
                  step={STEPS.POSITION}
                  onChange={(v) => updateMsTarget({ pz: v })}
                  hint_md={`Current: ${
                    msCurrent?.pz?.toFixed(DECIMALS.POSITION) ?? '???'
                  }`}
                  stepperLabel="pz"
                  stepperDecimals={DECIMALS.POSITION}
                  showStepper
                />
              </Flex>
            </Card>

            <Box gridColumn="span 2">
              <Card size={CARD_SIZE}>
                <Flex direction="column" gap={RADIX.FLEX.GAP.FORM}>
                  <Heading size={SUBHEADER_SIZE}>Orientation</Heading>

                  <Grid columns="4" gap={RADIX.FLEX.GAP.FORM}>
                    <CommonTextInput
                      id="qw"
                      label="qw"
                      value={msTarget.qw.toString()}
                      onFloatChange={(v) => updateMsTarget({ qw: v })}
                      hint_md={`Current: ${msCurrent?.qw ?? '???'}`}
                    />

                    <CommonTextInput
                      id="qx"
                      label="qx"
                      value={msTarget.qx.toString()}
                      onFloatChange={(v) => updateMsTarget({ qx: v })}
                      hint_md={`Current: ${msCurrent?.qx ?? '???'}`}
                    />

                    <CommonTextInput
                      id="qy"
                      label="qy"
                      value={msTarget.qy.toString()}
                      onFloatChange={(v) => updateMsTarget({ qy: v })}
                      hint_md={`Current: ${msCurrent?.qy ?? '???'}`}
                    />

                    <CommonTextInput
                      id="qz"
                      label="qz"
                      value={msTarget.qz.toString()}
                      onFloatChange={(v) => updateMsTarget({ qz: v })}
                      hint_md={`Current: ${msCurrent?.qz ?? '???'}`}
                    />
                  </Grid>
                </Flex>
              </Card>
            </Box>

            <Box gridColumn="span 2">
              <Card size={CARD_SIZE}>
                <Flex direction="column" gap={RADIX.FLEX.GAP.FORM}>
                  <Heading size={SUBHEADER_SIZE}>Other Controls</Heading>

                  <CommonFormGrid columns={2}>
                    <Box>
                      <CommonSelectInput
                        id="msdebug-training"
                        name="training"
                        label="Training"
                        options={['true', 'false'].map((b) => {
                          return {
                            label: b,
                            value: b,
                          };
                        })}
                        value={msTarget.training.toString()}
                        onBooleanChange={(v) => updateMsTarget({ training: v })}
                        hint_md={`Current: ${msCurrent?.training ?? '???'}`}
                      />
                    </Box>

                    <Box>
                      <CommonVideoPreview
                        identifier="MsDebugVideoPreview"
                        previewPx={msTarget.px}
                        previewPz={msTarget.pz}
                        videoID={msTarget.video_uuid}
                        selectConfig={{
                          releasePx: msTarget.px ?? 0,
                          onChange: (id) => updateMsTarget({ video_uuid: id }),
                        }}
                        delivery={delivery}
                        onChangeDelivery={setDelivery}
                        headerAsInput
                        hidePreviewButton
                        hidePlayback
                      />
                    </Box>
                  </CommonFormGrid>

                  <CommonFormGrid columns={3}>
                    <Box>
                      <Button
                        className="btn-block"
                        color={RADIX.COLOR.WARNING}
                        onClick={() =>
                          setMsTarget(getDefaultMS(machine.releaseFt))
                        }
                      >
                        Reset Target
                      </Button>
                    </Box>

                    <Box>
                      <Button
                        className="btn-block"
                        color={RADIX.COLOR.SEND_PITCH}
                        onClick={() => {
                          sendRawTarget(
                            getMsTarget(msTarget),
                            'ms debug',
                            false
                          );
                        }}
                      >
                        {t('common.load-pitch')}
                      </Button>
                    </Box>

                    {!calibrated && (
                      <Box>
                        <MachineCalibrateButton className="btn-block" />
                      </Box>
                    )}

                    <Box>
                      <Button
                        className="btn-block"
                        color={RADIX.COLOR.DANGER}
                        onClick={() => {
                          const data: IFireMsg = {
                            fire_id: 'ms-debug',
                            strikeZone: VISIBLE_STRIKEZONE,
                            hitterSide: null,
                          };

                          WebSocketService.send(
                            WsMsgType.U2S_Fire,
                            data,
                            COMPONENT_NAME
                          );
                        }}
                      >
                        Raw Fire
                      </Button>
                    </Box>

                    <CommonSelectInput
                      id="msdebug-special-ms"
                      placeholder="Special MS Target"
                      options={Object.values(SpecialMsPosition).map((o) => ({
                        label: o,
                        value: o,
                      }))}
                      name={'special-mstarget'}
                      onChange={(v) => {
                        const position = v as SpecialMsPosition;
                        if (position) {
                          NotifyHelper.success({
                            message_md: `Sending special mstarget \`${position}\` to machine!`,
                          });

                          specialMsTarget(position);
                        }
                      }}
                      optional
                    />

                    <Box>
                      <Button
                        className="btn-block"
                        color={RADIX.COLOR.INFO}
                        onClick={async () => {
                          const safeMsTargetMsg = getMsTarget(msTarget);
                          const safeMsTarget: IMachineState = {
                            model_id: '',
                            model_key: '',
                            ball_type: machine.ball_type,

                            tilt: safeMsTargetMsg.tilt,
                            yaw: safeMsTargetMsg.yaw,
                            training: safeMsTargetMsg.training,

                            px: safeMsTargetMsg.px,
                            py: safeMsTargetMsg.py,
                            pz: safeMsTargetMsg.pz,

                            w1: safeMsTargetMsg.w1,
                            w2: safeMsTargetMsg.w2,
                            w3: safeMsTargetMsg.w3,

                            a1: safeMsTargetMsg.a1,
                            a2: safeMsTargetMsg.a2,
                            a3: safeMsTargetMsg.a3,

                            qw: safeMsTargetMsg.qw,
                            qx: safeMsTargetMsg.qx,
                            qy: safeMsTargetMsg.qy,
                            qz: safeMsTargetMsg.qz,
                          };

                          const result =
                            await StateTransformService.getInstance().buildMs({
                              machineID: machine.machineID,
                              ms: safeMsTarget,
                            });

                          if (!result) {
                            return;
                          }

                          const payload: Partial<IPitch> = {
                            bs: result.bs,
                            traj: result.traj,
                            bs_hash: MachineHelper.getBsHash(
                              result.bs,
                              'debug'
                            ),
                            debugMs: safeMsTarget,
                          };

                          // open save pitch dialog
                          setDialogSave({
                            key: Date.now(),
                            pitch: payload,
                          });
                        }}
                      >
                        Save Pitch
                      </Button>
                    </Box>

                    <MachineInspectButton className="btn-block" />
                  </CommonFormGrid>
                </Flex>
              </Card>
            </Box>

            <Box gridColumn="span 2">
              <Card>
                <Heading size={SUBHEADER_SIZE}>Raw JSON of Current MS</Heading>
                <Skeleton loading={!msCurrent}>
                  <JsonViewer value={msCurrent} />
                </Skeleton>
              </Card>
            </Box>
          </Grid>
        }
        onClose={props.onClose}
      />

      {dialogSave && (
        <CopyPitchesDialog
          key={dialogSave.key}
          title={
            <Flex gap="2" align="center">
              {t('pd.add-to-pitch-list')}
              <Badge>Debug</Badge>
            </Flex>
          }
          identifier="MsDebugSavePitchDialog"
          reloadPitches={usePitchListStore.getState().reloadPitches}
          pitches={[dialogSave.pitch]}
          description="Your debug pitch is ready to be saved to a pitch list. Complete this form to save your pitch."
          onCreated={() => setDialogSave(undefined)}
          onClose={() => setDialogSave(undefined)}
          onGoToPitchList={() => closeDialog(MachineDialogMode.Debug)}
          disableScaling
        />
      )}
    </ErrorBoundary>
  );
};
